/*
  EarthOnMall global transition controller
  Install location: assets/eom-global-transition.js

  Key behavior:
  - Page-only navigation:
    Source page shows overlay immediately.
    Destination page is released by the head boot snippet after first paint.
    This JS does not wait for homepage hero, route loaders, skeletons, images, or virtualization.

  - Targeted section navigation:
    Keeps target/section wait behavior so direct section links still land correctly.

  - BFCache:
    If the browser restores a page from back/forward cache, any old overlay is cleared immediately.
*/

(function () {
  const SELECTORS = {
    overlay: '#eom-global-transition-overlay',
    headerGroup: '#header-group',
    anchor: '[data-eom-scroll-anchor]',
    noTransition: '[data-no-global-transition]',
    transitionOptIn: '[data-eom-transition]',
    handoff:
      [
        '[data-eom-route-ready]',
        '[data-eom-page-ready]',
        '[data-eom-section-ready]',
        '[data-eom-page-loader]',
        '[data-eom-section-loader]',
        '[data-eom-skeleton]',
        '[data-eom-route-loading]',
        '[data-eom-section-loading]',
        '[aria-busy="true"]',
        '.eom-before-checkout-people-loader',
        '.loading-overlay',
        '.loading__spinner',
        '.skeleton',
        '.skeleton-loader'
      ].join(',')
  };

  const STORAGE_KEY = 'eomPendingAnchorTransition';

  const SAME_PAGE_TARGET_WAIT = 450;
  const CROSS_PAGE_TARGET_WAIT = 700;
  const PAGE_ONLY_FALLBACK_FRAMES = 1;
  const HEADER_ADJUSTMENT = -2;

  let isRunning = false;
  let activeGate = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function now() {
    return Date.now();
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  function nextFrame() {
    return new Promise((resolve) => {
      window.requestAnimationFrame(resolve);
    });
  }

  async function waitForFrames(count) {
    for (let index = 0; index < count; index += 1) {
      await nextFrame();
    }
  }

  function parsePending() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function setPending(data) {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          Object.assign(
            {
              startedAt: now()
            },
            data
          )
        )
      );
    } catch (error) {
      /* Ignore storage failures. */
    }
  }

  function clearPending() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* Ignore storage failures. */
    }

    document.documentElement.classList.remove('eom-transition-pending');
    document.documentElement.classList.remove('eom-transition-booting');
    document.documentElement.classList.remove('eom-transition-page-only');
    document.documentElement.classList.remove('eom-transition-boot-mask');
    document.documentElement.classList.remove('eom-transition-releasing');
  }

  function getOverlay() {
    return document.querySelector(SELECTORS.overlay);
  }

  function getHeaderOffset() {
    const headerGroup = document.querySelector(SELECTORS.headerGroup);
    if (!headerGroup) return 0;

    return Math.max(0, Math.round(headerGroup.getBoundingClientRect().height + HEADER_ADJUSTMENT));
  }

  function updateHeaderOffsetVariable() {
    document.documentElement.style.setProperty('--eom-header-offset', `${getHeaderOffset()}px`);
  }

  function decodeHash(hash) {
    if (!hash || hash === '#') return '';

    try {
      return decodeURIComponent(hash.slice(1));
    } catch (error) {
      return hash.slice(1);
    }
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function getTargetFromHash(hash) {
    const id = decodeHash(hash);
    if (!id) return null;

    return document.getElementById(id) || document.querySelector(`${SELECTORS.anchor}[id="${cssEscape(id)}"]`);
  }

  function getTargetFromSelector(selector) {
    if (!selector) return null;

    try {
      return document.querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  function getTarget(targetRef, mode) {
    return mode === 'selector' ? getTargetFromSelector(targetRef) : getTargetFromHash(targetRef);
  }

  function getHandoffElement() {
    try {
      return document.querySelector(SELECTORS.handoff);
    } catch (error) {
      return null;
    }
  }

  function getMainContent() {
    return (
      document.querySelector('#MainContent') ||
      document.querySelector('main') ||
      document.querySelector('[role="main"]')
    );
  }

  function hasUsablePageShell() {
    const main = getMainContent();
    return Boolean(main && main.children && main.children.length > 0);
  }

  function getTargetY(target) {
    updateHeaderOffsetVariable();
    return Math.max(0, target.getBoundingClientRect().top + window.scrollY - getHeaderOffset());
  }

  function samePath(url) {
    const clean = (value) => value.replace(/\/$/, '');
    return clean(url.pathname) === clean(window.location.pathname);
  }

  function isModifiedClick(event) {
    return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
  }

  function isSkippableUrl(url) {
    const path = url.pathname;

    return (
      url.origin !== window.location.origin ||
      path.startsWith('/cart') ||
      path.startsWith('/checkout') ||
      path.startsWith('/account') ||
      path.startsWith('/admin') ||
      path.startsWith('/tools')
    );
  }

  function shouldSkipLink(link, url, event) {
    if (!link || link.closest(SELECTORS.noTransition)) return true;
    if (isModifiedClick(event)) return true;
    if (link.target && link.target !== '_self') return true;
    if (link.hasAttribute('download')) return true;
    if (isSkippableUrl(url)) return true;
    return false;
  }

  function getOptInTarget(link) {
    const host = link.closest(SELECTORS.transitionOptIn);
    return host?.getAttribute('data-eom-transition-target') || link.getAttribute('data-eom-transition-target') || '';
  }

  function showOverlay(options = {}) {
    if (prefersReducedMotion()) return false;

    const overlay = getOverlay();

    document.documentElement.classList.add('eom-transition-pending');
    document.documentElement.classList.remove('eom-transition-booting');
    document.documentElement.classList.remove('eom-transition-page-only');

    document.body.classList.toggle('eom-is-transitioning', options.animate !== false);

    if (overlay) {
      overlay.classList.remove('is-exiting');
      overlay.classList.add('is-active');
      overlay.classList.add('is-loading');

      overlay.getBoundingClientRect();
    }

    return true;
  }

  function hideOverlay() {
    const overlay = getOverlay();

    if (overlay) {
      overlay.classList.remove('is-active');
      overlay.classList.remove('is-loading');
      overlay.classList.remove('is-exiting');
    }

    document.body.classList.remove('eom-is-transitioning');

    document.documentElement.classList.remove('eom-transition-pending');
    document.documentElement.classList.remove('eom-transition-booting');
    document.documentElement.classList.remove('eom-transition-page-only');
    document.documentElement.classList.remove('eom-transition-boot-mask');
    document.documentElement.classList.remove('eom-transition-releasing');

    isRunning = false;
    activeGate = null;
  }

  function beginTransition(options = {}) {
    if (activeGate) return activeGate;

    isRunning = true;

    const shown = showOverlay({
      animate: options.animate !== false
    });

    activeGate = {
      shown,
      close() {
        clearPending();
        hideOverlay();
      }
    };

    return activeGate;
  }

  function replaceHash(hash) {
    if (!hash || window.location.hash === hash) return;
    window.history.replaceState(null, '', hash);
  }

  function pushHash(hash) {
    if (!hash || window.location.hash === hash) return;
    window.history.pushState(null, '', hash);
  }

  async function scrollToTarget(target, hash, options = {}) {
    if (hash) {
      if (options.pushHash) pushHash(hash);
      else replaceHash(hash);
    }

    await nextFrame();

    window.scrollTo({
      top: getTargetY(target),
      behavior: 'auto'
    });

    await nextFrame();

    window.scrollTo({
      top: getTargetY(target),
      behavior: 'auto'
    });

    if (!target.matches(SELECTORS.anchor)) {
      target.focus?.({ preventScroll: true });
    }
  }

  async function waitForTargetOrHandoff(targetRef, mode, timeoutMs) {
    const started = now();

    while (now() - started < timeoutMs) {
      const target = getTarget(targetRef, mode);

      if (target) {
        await nextFrame();
        return {
          type: 'target',
          target
        };
      }

      const handoff = getHandoffElement();

      if (handoff || hasUsablePageShell()) {
        await nextFrame();
        return {
          type: 'handoff',
          target: null
        };
      }

      await wait(25);
    }

    return {
      type: 'timeout',
      target: getTarget(targetRef, mode)
    };
  }

  async function finishPageOnlyFallback(options = {}) {
    const gate = options.gate || beginTransition({ animate: options.animate !== false });

    await waitForFrames(PAGE_ONLY_FALLBACK_FRAMES);

    if (gate) gate.close();
    else {
      clearPending();
      hideOverlay();
    }

    return true;
  }

  async function revealTarget(targetRef, options = {}) {
    const mode = options.mode || 'hash';
    const gate = options.gate || beginTransition({ animate: options.animate !== false });

    const result = await waitForTargetOrHandoff(
      targetRef,
      mode,
      options.crossPage ? CROSS_PAGE_TARGET_WAIT : SAME_PAGE_TARGET_WAIT
    );

    if (result.target) {
      await scrollToTarget(result.target, mode === 'hash' ? targetRef : null, {
        pushHash: options.pushHash === true
      });
    }

    if (gate) gate.close();
    else {
      clearPending();
      hideOverlay();
    }

    return Boolean(result.target || result.type === 'handoff');
  }

  function navigateAfterOverlayPaint(url) {
    window.requestAnimationFrame(() => {
      window.location.assign(url.href);
    });
  }

  function handleDocumentClick(event) {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const url = new URL(link.href, window.location.href);
    if (shouldSkipLink(link, url, event)) return;

    const isSamePage = samePath(url);
    const hasHash = Boolean(url.hash && url.hash !== '#');
    const optIn = Boolean(link.closest(SELECTORS.transitionOptIn));
    const optInTarget = getOptInTarget(link);

    if (isSamePage && hasHash) {
      event.preventDefault();

      if (isRunning) return;

      const gate = beginTransition({ animate: true });

      revealTarget(url.hash, {
        mode: 'hash',
        pushHash: true,
        gate
      });

      return;
    }

    if (isSamePage && optIn && optInTarget) {
      event.preventDefault();

      if (isRunning) return;

      const gate = beginTransition({ animate: true });

      revealTarget(optInTarget, {
        mode: 'selector',
        gate
      });

      return;
    }

    if (isSamePage && optIn) {
      event.preventDefault();

      if (isRunning) return;

      const gate = beginTransition({ animate: true });

      finishPageOnlyFallback({
        gate
      });

      return;
    }

    if (isSamePage) return;

    event.preventDefault();

    beginTransition({ animate: true });

    if (hasHash) {
      setPending({
        type: 'target',
        mode: 'hash',
        target: url.hash,
        href: url.href
      });
    } else {
      setPending({
        type: 'page',
        href: url.href
      });
    }

    navigateAfterOverlayPaint(url);
  }

  async function handleInitialTransition() {
    if (window.__EOM_PAGE_ONLY_TRANSITION_RELEASED__) {
      clearPending();
      hideOverlay();
      return;
    }

    const pending = parsePending();

    if (pending?.type === 'target' && pending.target) {
      const gate = beginTransition({
        animate: false
      });

      await revealTarget(pending.target, {
        animate: false,
        pending,
        mode: pending.mode || 'hash',
        crossPage: true,
        gate
      });

      return;
    }

    /*
      This is only a fallback for cases where the head boot snippet was not installed.
      With the new boot snippet, page-only pending is removed before this deferred file matters.
    */
    if (pending?.type === 'page') {
      const gate = beginTransition({
        animate: false
      });

      await finishPageOnlyFallback({
        animate: false,
        pending,
        gate
      });

      return;
    }

    if (window.location.hash && window.location.hash !== '#') {
      await revealTarget(window.location.hash, {
        transition: false,
        mode: 'hash',
        crossPage: false
      });

      return;
    }

    clearPending();
  }

  function exposeApi() {
    window.EOMGlobalTransition = Object.assign(window.EOMGlobalTransition || {}, {
      show: () => beginTransition({ animate: false }),
      hide: () => {
        clearPending();
        hideOverlay();
      },
      markReady: () => {
        document.dispatchEvent(new CustomEvent('eom:route-ready'));
      },
      markPageReady: () => {
        document.dispatchEvent(new CustomEvent('eom:page-ready'));
      },
      markSectionReady: () => {
        document.dispatchEvent(new CustomEvent('eom:section-ready'));
      }
    });
  }

  function handlePageShow(event) {
    /*
      Critical for homepage/back-button issues.
      If the browser restores a page from BFCache, it can restore the DOM exactly
      as it was when navigation started, including an active transition overlay.
    */
    if (event.persisted) {
      clearPending();
      hideOverlay();
    }
  }

  function init() {
    exposeApi();

    document.documentElement.classList.add('eom-global-transition-ready');

    updateHeaderOffsetVariable();

    document.addEventListener('click', handleDocumentClick, true);
    window.addEventListener('resize', updateHeaderOffsetVariable);
    window.addEventListener('orientationchange', updateHeaderOffsetVariable);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('shopify:section:load', updateHeaderOffsetVariable);

    handleInitialTransition();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/*
  Before Checkout group-photo loader bridge.
  Keeps the CSS placeholder visible until the virtualized foreground image has loaded.
*/
(function () {
  const SECTION_SELECTOR = '[data-eom-people-loader-section]';
  const IMAGE_SELECTOR = '.eom-faq-product-mount img.eom-faq-product-image';
  const BOUND_ATTR = 'data-eom-people-loader-bound';
  const READY_ATTR = 'data-eom-people-image-ready';

  function markReady(section) {
    section.setAttribute(READY_ATTR, 'true');
    document.dispatchEvent(new CustomEvent('eom:section-ready'));
  }

  function markWaiting(section) {
    section.removeAttribute(READY_ATTR);
  }

  function bindSection(section) {
    if (!section || section.hasAttribute(BOUND_ATTR)) return;
    section.setAttribute(BOUND_ATTR, 'true');

    let fallbackTimer = null;

    const clearFallback = () => {
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      fallbackTimer = null;
    };

    const evaluate = () => {
      const image = section.querySelector(IMAGE_SELECTOR);

      if (!image) {
        clearFallback();
        markWaiting(section);
        return;
      }

      if (image.complete && image.naturalWidth > 0) {
        clearFallback();
        markReady(section);
        return;
      }

      markWaiting(section);

      image.addEventListener(
        'load',
        () => {
          clearFallback();
          markReady(section);
        },
        { once: true }
      );

      image.addEventListener(
        'error',
        () => {
          clearFallback();
          markReady(section);
        },
        { once: true }
      );

      clearFallback();
      fallbackTimer = window.setTimeout(() => {
        markReady(section);
      }, 4500);
    };

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(evaluate);
    });

    observer.observe(section, {
      childList: true,
      subtree: true
    });

    evaluate();
  }

  function initPeopleLoaders(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SECTION_SELECTOR).forEach(bindSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPeopleLoaders(document), { once: true });
  } else {
    initPeopleLoaders(document);
  }

  window.addEventListener('load', () => initPeopleLoaders(document));
  document.addEventListener('shopify:section:load', (event) => initPeopleLoaders(event.target));
})();